import axios from 'axios';
import { all, call, fork, put, takeEvery, takeLeading } from 'redux-saga/effects';
import { ToastManager } from '../../components/Toast/ToastManager';
import {
    candidateCallEndFailure,
    candidateCallEndSuccess,
    candidateCallStartFailure,
    candidateCallStartSuccess,
    compareJDCVFailure,
    compareJDCVSuccess,
    contactAdminFailure,
    contactAdminSuccess,
    createAndUpdateScreenshotsFailure,
    createAndUpdateScreenshotsSuccess,
    createCodeSubmissionFailure,
    createCodeSubmissionSuccess,
    faceExpressionFailure,
    faceExpressionSuccess,
    fetchScreenShotImageFailure,
    fetchScreenShotImageSuccess,
    fullFaceDetectionFailure,
    fullFaceDetectionNewFailure,
    fullFaceDetectionNewSuccess,
    fullFaceDetectionSuccess,
    getCandidateDetailsUsingResumeFailure,
    getCandidateDetailsUsingResumeSuccess,
    getConversionReportFailure,
    getConversionReportSuccess,
    getInterviewFailure,
    getInterviewSuccess,
    getInterviewTokenFailure,
    getInterviewTokenSuccess,
    getQuestionFailure,
    getQuestionNumberFailure,
    getQuestionNumberSuccess,
    getQuestionSuccess,
    getSessionTokenFailure,
    getSessionTokenSuccess,
    interviewFeedbackStatusFailure,
    interviewFeedbackStatusSuccess,
    liveKitTokenFailure,
    liveKitTokenSuccess,
    lookingForwardFailure,
    lookingForwardSuccess,
    movementFailure,
    movementSuccess,
    notificationErrorFailure,
    notificationErrorSuccess,
    postCandidateFailure,
    postCandidateSuccess,
    postConsentFailure,
    postConsentSuccess,
    postInterviewFeedbackFailure,
    postInterviewFeedbackSuccess,
    postInterviewStatusFailure,
    postInterviewStatusSuccess,
    practiceQuestionFailure,
    practiceQuestionSuccess,
    practiceQuestionUpdateFailure,
    practiceQuestionUpdateSuccess,
    resendOtpRequestFailure,
    resendOtpRequestSuccess,
    saveVoiceReferenceFailure,
    saveVoiceReferenceSuccess,
    submitCefrFailure,
    submitCefrSuccess,
    submitCodingQuestionFailure,
    submitCodingQuestionSuccess,
    submitMcqFailure,
    submitMcqSuccess,
    submitTranscribeFailure,
    submitTranscribeSuccess,
    submitWrittenTestFailure,
    submitWrittenTestSuccess,
    switchTabFailure,
    switchTabSuccess,
    UpdateConversationQuestionFailure,
    UpdateConversationQuestionSuccess,
    updateTranscriptFailure,
    updateTranscriptSuccess,
    uploadCandidateVideoFailure,
    uploadCandidateVideoSuccess,
    verifyCandidateKeyFailure,
    verifyCandidateKeySuccess,
    verifyRoomFailure,
    verifyRoomSuccess,
} from '../action';
import {
    CANDIDATE_CALL_END,
    CANDIDATE_CALL_START,
    COMPARE_JD_CV,
    CONTACT_ADMIN,
    CREATE_AND_UPDATE_SCREENSHOT,
    CREATE_CODE_SUBMISSION,
    FACE_EXPRESSION,
    FETCH_SCREENSHOT_IMAGE,
    FULL_FACE_DETECTION,
    FULL_FACE_DETECTION_NEW,
    GET_CANDIDATE_DETAILS_USING_RESUME,
    GET_CONVERSION_REPORT,
    GET_INTERVIEW,
    GET_INTERVIEW_TOKEN,
    GET_QUESTION,
    GET_QUESTION_NUMBER,
    GET_SESSION_TOKEN,
    INTERVIEW_FEEDBACK_STATUS,
    LIVE_KIT_TOKEN,
    LOOKING_FORWARD,
    MOVEMENT,
    NOTIFICATION_ERROR,
    POST_CANDIDATE,
    POST_CONSENT,
    POST_INTERVIEW_FEEDBACK,
    POST_INTERVIEW_STATUS,
    PRACTICE_QUESTION,
    PRACTICE_QUESTION_UPDATE,
    RESEND_OTP_REQUEST,
    SAVE_VOICE_REFERENCE,
    SUBMIT_CEFR,
    SUBMIT_CODING_QUESTION,
    SUBMIT_MCQ,
    SUBMIT_TRANSCRIBE,
    SUBMIT_WRITTEN_TEST,
    SWITCH_TAB,
    UPDATE_CONVERSATION_QUESTION,
    UPDATE_TRANSCRIPT,
    UPLOAD_CANDIDATE_VIDEO,
    UPLOAD_CEFR_VIDEO,
    UPLOAD_VIDEO,
    VERIFY_CANDIDATE_KEY,
    VERIFY_ROOM,
} from '../action/types';
import { startReplayable, submitAnswerWithRetry } from '../../utils/api/answerSubmitRetry';
import INTERVIEW from '../../utils/api/interview';
import { isTransientError, withCapacityRetry } from '../../utils/api/capacityRetry';
import SYSTEM from '../../utils/api/system';
import VIDEO from '../../utils/api/video';
import { generateUrl, tenantId } from '../../utils/helper';
import { GENERAL_ERROR } from '../../common/constants';

function* faceExpressionRequest(action) {
    try {
        const res = yield INTERVIEW.post(`faceExpression`, action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(faceExpressionSuccess());
            if (action.payload.callback) yield call(action.payload.callback, res?.data?.result);
        }
    } catch (e) {
        yield put(faceExpressionFailure());
    }
}

function* saveVoiceReferenceRequest(action) {
    try {
        const formData = new FormData();
        formData.append('roomId', action.payload.roomId);
        formData.append('voiceReference', action.payload.audioBlob, 'voice-reference.webm');
        const res = yield INTERVIEW.post('saveVoiceReference', formData, {
            headers: { 'content-type': 'multipart/form-data' },
        });
        if (res.status === 200 || res.status === 201) {
            yield put(saveVoiceReferenceSuccess());
            if (action.payload.callback) yield call(action.payload.callback, res?.data?.result);
        } else {
            yield put(saveVoiceReferenceFailure());
            if (action.payload.failure) yield call(action.payload.failure);
        }
    } catch (e) {
        yield put(saveVoiceReferenceFailure());
        if (action.payload?.failure) yield call(action.payload.failure);
    }
}

const CANDIDATE_VIDEO_UPLOAD_STALL_MS = 90000;
const CANDIDATE_VIDEO_UPLOAD_END_WAIT_MS = 20000;
const CANDIDATE_VIDEO_UPLOAD_ATTEMPTS = 3;
const CANDIDATE_VIDEO_UPLOAD_RETRY_DELAY_MS = 3000;
let candidateVideoUploads = Promise.resolve();
let candidateCallEnding = false;
const waitForCandidateVideoUploads = (deadline) => Promise.race([candidateVideoUploads, deadline]);

function* uploadCandidateVideoRequest(action) {
    const previousUpload = candidateVideoUploads;
    let releaseUpload;
    candidateVideoUploads = new Promise((resolve) => (releaseUpload = resolve));
    // const controller = new AbortController();
    let controller = new AbortController();
    let stallTimer;
    const armStallTimer = () => {
        clearTimeout(stallTimer);
        stallTimer = setTimeout(() => controller.abort(), CANDIDATE_VIDEO_UPLOAD_STALL_MS);
    };
    let uploadSent = false;
    const trackUploadProgress = (e) => {
        uploadSent = e.total > 0 && e.loaded >= e.total;
        armStallTimer();
    };
    try {
        yield previousUpload;
        const { formData } = action.payload || new FormData();
        // armStallTimer();
        // const res = yield INTERVIEW.post(`uploadCandidateVideo`, formData);
        // const res = yield INTERVIEW.post(`uploadCandidateVideo`, formData, {
        //     signal: controller.signal,
        //     onUploadProgress: armStallTimer,
        //     capacityRetryMax: 1,
        // });
        let res;
        for (let attempt = 1; !res; attempt++) {
            controller = new AbortController();
            uploadSent = false;
            armStallTimer();
            try {
                res = yield INTERVIEW.post(`uploadCandidateVideo`, formData, {
                    signal: controller.signal,
                    onUploadProgress: trackUploadProgress,
                    capacityRetryMax: 1,
                });
            } catch (err) {
                if (
                    (err?.response && err.response.status !== 408) ||
                    (!err?.response && uploadSent) ||
                    attempt >= CANDIDATE_VIDEO_UPLOAD_ATTEMPTS
                )
                    throw err;
                clearTimeout(stallTimer);
                yield new Promise((resolve) => setTimeout(resolve, CANDIDATE_VIDEO_UPLOAD_RETRY_DELAY_MS));
            }
        }
        if (res.status === 200 || res.status === 201) {
            yield put(uploadCandidateVideoSuccess());
            if (action.payload.callback) yield call(action.payload.callback, res?.data?.result);
        }
    } catch (e) {
        yield put(uploadCandidateVideoFailure());
    } finally {
        clearTimeout(stallTimer);
        releaseUpload();
    }
}

function* getSessionTokenRequest(action) {
    try {
        const { keys } = action.payload;
        const res = yield INTERVIEW.post(`session`, { keys });
        if (res.status === 200 || res.status === 201) {
            yield put(getSessionTokenSuccess());
            yield call(action.payload.callback, res?.data?.result);
        }
    } catch (e) {
        yield put(getSessionTokenFailure());
    }
}

function* updateTranscriptRequest(action) {
    try {
        const res = yield INTERVIEW.post(`updateTranscript/${action.payload.roomId}`, action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(updateTranscriptSuccess());
        }
    } catch (e) {
        yield put(updateTranscriptFailure());
    }
}
function* practiceQuestionRequest(action) {
    try {
        const res = yield INTERVIEW.get(`practiceInterview/${action.payload.roomId}`);
        if (res.status === 200 || res.status === 201) {
            yield put(practiceQuestionSuccess());
            yield call(action.payload.callback, res?.data?.result?.practiceInterview);
        }
    } catch (e) {
        yield put(practiceQuestionFailure());
    }
}

function* liveKitTokenRequest(action) {
    try {
        const isBeam = action.payload.role === 'beam';
        const res = yield isBeam
            ? INTERVIEW.get(`getTokenBeam/${action.payload.roomId}/${action.payload.mode}`)
            : INTERVIEW.get(`getToken/${action.payload.roomId}/${action.payload.mode}/${action.payload.role}`);
        if (res.status === 200 || res.status === 201) {
            yield put(liveKitTokenSuccess());
            yield call(action.payload.callback, res?.data?.result);
        }
    } catch (e) {
        yield put(liveKitTokenFailure());
    }
}

function* practiceQuestionUpdateRequest(action) {
    try {
        const res = yield INTERVIEW.post(`practiceInterview/${action.payload.roomId}`, {});
        if (res.status === 200 || res.status === 201) {
            yield put(practiceQuestionUpdateSuccess());
        }
    } catch (e) {
        yield put(practiceQuestionUpdateFailure());
    }
}

function* notificationErrorRequest(action) {
    try {
        const envMode = process.env.REACT_APP_ENV;
        if (envMode === 'prod' || envMode === 'test') {
            let res = yield INTERVIEW.post(`sendMessageInMail`, action.payload);
            if (res.status === 200 || res.status === 201) {
                yield put(notificationErrorSuccess());
            }
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        yield put(notificationErrorFailure());
    }
}
function* submitTranscribeRequest(action) {
    try {
        let res = yield INTERVIEW.post(`transcribe`, action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(submitTranscribeSuccess(res.data.result));
        }
    } catch (e) {
        yield put(submitTranscribeFailure());
    }
}
function* UpdateConversationQuestionRequest(action) {
    try {
        let res = yield INTERVIEW.post(`updateConQuestion`, action.payload);
        if (res.status === 200 || res.status === 201) yield put(UpdateConversationQuestionSuccess());
    } catch (e) {
        yield put(UpdateConversationQuestionFailure());
    }
}
function* movementRequest(action) {
    try {
        let res = yield INTERVIEW.patch('movement', action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(movementSuccess(res.data.result));
        } else yield put(movementFailure());
    } catch (e) {
        yield put(movementFailure());
    }
}
function* switchTabRequest(action) {
    const { callback, ...body } = action.payload || {};
    if (candidateCallEnding) return;
    let logged = false;
    try {
        // const { callback, ...body } = action.payload || {};
        let res = yield INTERVIEW.patch('switchTab', body);
        logged = true;
        if (res.status === 200 || res.status === 201) {
            if (callback) {
                yield call(callback);
            }
            yield put(switchTabSuccess(res.data.result));
        }
    } catch (e) {
        yield put(switchTabFailure());
        if (callback && !logged) {
            yield call(callback);
        }
    }
}
function* candidateCallStartRequest(action) {
    try {
        let res = yield INTERVIEW.put(`callStart/${action.payload.roomId}`, {
            ipAddress: action.payload.ipAddress || '',
        });
        if (res.status === 200 || res.status === 201) {
            if (res?.data?.result?.isInterviewCanceled) {
                yield put(candidateCallStartFailure());
                ToastManager.addToast({ title: 'This interview has been cancelled' });
            } else {
                if (res.data.result?.token) localStorage.setItem('token', res.data.result?.token);
                yield call(action.payload.callback, res?.data?.result);
                yield put(candidateCallStartSuccess(res.data.result));
                if (!res?.data?.result?.status) ToastManager.addToast({ title: 'This meeting has already started.' });
            }
        }
    } catch (e) {
        yield put(candidateCallStartFailure());
        if (e?.response?.data?.message) ToastManager.addToast({ title: e?.response?.data?.message });
        else ToastManager.addToast({ title: 'This meeting has already started.' });
    }
}
function* candidateCallEndRequest(action) {
    const { doFinally, endCallStatus, roomId } = action.payload;
    const endDeadline = new Promise((resolve) => setTimeout(resolve, CANDIDATE_VIDEO_UPLOAD_END_WAIT_MS));
    if (doFinally && endCallStatus !== 'Browser Closed') candidateCallEnding = true;
    try {
        // const res = yield INTERVIEW.post(`callEnd/${roomId}`, {
        //     ipAddress: action.payload.ipAddress || '',
        //     endCallStatus: endCallStatus ?? 'Call End',
        // });
        const res = yield call(
            withCapacityRetry,
            () =>
                INTERVIEW.post(`callEnd/${roomId}`, {
                    ipAddress: action.payload.ipAddress || '',
                    endCallStatus: endCallStatus ?? 'Call End',
                }),
            isTransientError
        );
        if (res.status === 200 || res.status === 201) {
            yield put(candidateCallEndSuccess());
        }
    } catch (e) {
        yield put(candidateCallEndFailure());
    } finally {
        if (doFinally && endCallStatus !== 'Browser Closed') yield call(waitForCandidateVideoUploads, endDeadline);
        if (doFinally) doFinally();
    }
}
function* contactAdminRequest(action) {
    try {
        const res = yield VIDEO.post(`contactAdmin`, action.payload);
        if (res.status === 201) {
            yield put(contactAdminSuccess(res.data));
        }
    } catch (e) {
        yield put(contactAdminFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}
function* createAndUpdateScreenshotsRequest(action) {
    try {
        const { callback } = action.payload;
        if (callback) delete action.payload.callback;
        let res = yield INTERVIEW.post(`createAndUpdateScreenshots/${action.payload.roomId}`, action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(createAndUpdateScreenshotsSuccess(res.data.result));
            if (callback) yield call(callback);
        } else {
            yield put(createAndUpdateScreenshotsFailure());
            if (callback) yield call(callback);
        }
    } catch (e) {
        yield put(createAndUpdateScreenshotsFailure());
    }
}
function* fetchScreenShotImageRequest(action) {
    try {
        const res = yield INTERVIEW.post('dataURIUpload', action.payload);
        if (res.status === 201) {
            if (action.payload.type !== 10) {
                yield put(fetchScreenShotImageSuccess(res.data.result));
            } else {
                yield call(action.payload.callback, res?.data?.result?.url);
            }
        }
    } catch (e) {
        yield put(fetchScreenShotImageFailure());
    }
}
export function* getInterviewRequest(action) {
    try {
        const { interviewId, roomId, expires, signature } = action.payload;

        const token = localStorage.getItem('token');
        const headers = {};

        if (token) headers.Authorization = `Bearer ${token}`;

        const baseURL = generateUrl('interview_system');
        const url = `${baseURL}/incbot/getInterview/${interviewId}/${roomId}?expires=${expires}&signature=${signature}`;

        const res = yield axios.get(url, { headers });

        if (res.status === 200) {
            yield put(getInterviewSuccess(res.data.result));
        } else {
            yield put(getInterviewFailure());
        }
    } catch (e) {
        yield put(getInterviewFailure());
        if (e?.response?.data?.message) {
            ToastManager.addToast({
                title: e.response.data.message,
            });
        }
    }
}

function* getInterviewTokenRequest(action) {
    try {
        const token = localStorage.getItem('token');
        const headers = {};

        if (token) headers.Authorization = `Bearer ${token}`;

        const baseURL = generateUrl('interview_backend');
        const res = yield axios.get(
            `${baseURL}/getInterviewToken/${action.payload.positionId}/${action.payload.questionId}/${action.payload.linkId}`,
            { headers }
        );
        if (res.data?.result) {
            localStorage.setItem('tenantId', res.data?.result?.tenantId);
        }
        if (res.status === 200) {
            yield put(getInterviewTokenSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getInterviewTokenFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}

function* resendOtpRequest(action) {
    try {
        const res = yield INTERVIEW.get(`reSendOtp/${action.payload.interviewId}`);
        if (res.status === 200) {
            yield put(resendOtpRequestSuccess(res.data.result));
        }
    } catch (e) {
        yield put(resendOtpRequestFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}

function* postCandidateRequest(action) {
    try {
        const formData = new FormData();
        formData.append('position', action.payload.form.position);
        formData.append('question', action.payload.form.question);
        formData.append('candidate', JSON.stringify(action.payload.form.candidate));
        formData.append('expiredAt', action.payload.form.expiredAt);
        if (action.payload.form.startAt) formData.append('startAt', action.payload.form.startAt);
        if (action.payload.form.linkExpiryType) formData.append('linkExpiryType', action.payload.form.linkExpiryType);
        if (action.payload.form.user) formData.append('user', action.payload.form.user);
        if (action.payload.form.createdBy) formData.append('createdBy', action.payload.form.createdBy);
        if (action.payload.form.resume) formData.append('resume', action.payload.form.resume);
        if (action.payload.form.linkExpiresIn) {
            formData.append('linkExpiresIn', action.payload.form.linkExpiresIn);
        }
        if (action.payload.form.timezone) {
            formData.append('timezone', JSON.stringify(action.payload.form.timezone));
        }
        const tenantId = localStorage.getItem('tenantId');
        const res = yield SYSTEM.post(`incbot/postCandidate`, formData, {
            headers: {
                'content-type': 'multipart/form-data',
                ...(tenantId && { 'x-tenant-id': tenantId }),
            },
        });
        if (res.status === 200 || res.status === 201) {
            let data = res.data.result;
            yield put(postCandidateSuccess(data));
            yield call(action.payload.callback, data);
        } else yield put(postCandidateFailure());
    } catch (e) {
        yield put(postCandidateFailure());
        const rawMessage = e?.response?.data?.message;
        if (rawMessage) {
            const fieldFriendlyNames = {
                'candidate.phone': 'phone number',
                'candidate.email': 'email address',
                'candidate.name': 'name',
            };
            const matchedField = Object.keys(fieldFriendlyNames).find((field) => rawMessage.includes(`"${field}"`));
            ToastManager.addToast({
                title: matchedField
                    ? rawMessage.replace(`"${matchedField}"`, fieldFriendlyNames[matchedField])
                    : rawMessage,
            });
        }
    }
}
function* postInterviewFeedbackRequest(action) {
    try {
        const res = yield INTERVIEW.post(`postInterviewFeedback`, action.payload.data);
        if (res.status === 201) {
            yield put(postInterviewFeedbackSuccess());
            yield call(action.payload.callback);
            ToastManager.addToast({
                title: 'Interview feedback submitted successfully.',
            });
        }
    } catch (e) {
        yield put(postInterviewFeedbackFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}
function* verifyRoomRequest(action) {
    try {
        const token = localStorage.getItem('token');
        const headers = {};

        if (token) headers.Authorization = `Bearer ${token}`;

        // Remove old tenantId before verification
        localStorage.removeItem('tenantId');

        const baseURL = generateUrl('interview_backend');
        const url = `${baseURL}/verifyRoom/${action.payload.roomId}`;

        // const res = yield axios.get(url, { headers });
        const res = yield call(withCapacityRetry, () => axios.get(url, { headers }));
        if (res.status === 200) {
            yield put(verifyRoomSuccess(res.data.result));
            localStorage.setItem('token', res.data.result?.token || '');
            if (res.data.result?.tenantId) {
                localStorage.setItem('tenantId', res.data.result?.tenantId);
            }
        } else yield put(verifyRoomFailure());
    } catch (e) {
        yield put(verifyRoomFailure());
    }
}
function* verifyCandidateKeyRequest(action) {
    try {
        const { interviewId, key, interviewLanguage, voiceName, languageName } = action.payload?.form || {};
        let data = {};
        if (interviewLanguage) data.interviewLanguage = interviewLanguage;
        if (voiceName) data.voiceName = voiceName;
        if (languageName) data.languageName = languageName;
        const res = yield INTERVIEW.post(`verifyCandidate/${interviewId}/${key}`, data);
        if (res.status === 200) {
            yield put(verifyCandidateKeySuccess(res.data.result));
            yield call(action.payload.callback, res.data.result?.status);
        }
    } catch (e) {
        yield put(verifyCandidateKeyFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}

function* getQuestionNumberRequest(action) {
    try {
        const res = yield INTERVIEW.get(`completedQuestions/${action.payload?.interviewId}`);
        if (res.status === 200 || res.status === 201) {
            yield put(getQuestionNumberSuccess(res.data.result));
        } else yield put(getQuestionNumberFailure());
    } catch (e) {
        yield put(getQuestionNumberFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}

function* getQuestionRequest(action) {
    try {
        const res = yield INTERVIEW.post('incbotResponse', action.payload.data);
        if (res.status === 200 || res.status === 201) {
            yield put(getQuestionSuccess(res.data.result));
            if (action.payload?.callback) {
                yield call(action.payload.callback, res.data.result);
            }
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        yield put(getQuestionFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: GENERAL_ERROR,
            });
    }
}
function* interviewFeedbackStatusRequest(action) {
    try {
        const res = yield INTERVIEW.get(`interviewFeedbackStatus/${action.payload.roomId}`);
        if (res.status === 200) {
            yield put(interviewFeedbackStatusSuccess(res.data.result));
        }
    } catch (e) {
        yield put(interviewFeedbackStatusFailure());
    }
}

function* fullFaceDetectionNewRequest(action) {
    const { data, onSuccess, onFailure, doFinally } = action.payload ?? {
        data: null,
        onSuccess: () => {},
        onFailure: () => {},
        doFinally: () => {},
    };
    try {
        const res = yield INTERVIEW.post('precheck-face-detect', data);
        if (res.status === 200) {
            const { detect, face_detection_message } = res.data ?? {
                detect: false,
                face_detection_message: '',
            };
            yield put(fullFaceDetectionNewSuccess(data?.image_base64));
            yield call(onSuccess, detect, face_detection_message);
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (e) {
        yield put(fullFaceDetectionNewFailure(e));
        yield call(onFailure, e);
    } finally {
        if (doFinally) doFinally();
    }
}

function* postInterviewStatusRequest(action) {
    const { doFinally } = action.payload;
    try {
        const res = yield INTERVIEW.patch('/updateInterviewStatus', action.payload);
        if (res.status === 200 || res.status === 201) yield put(postInterviewStatusSuccess());
        else yield put(postInterviewStatusFailure);
    } catch (e) {
        yield put(postInterviewStatusFailure);
    } finally {
        if (doFinally) doFinally();
    }
}

function* postCodingRecording(payload) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
        if (key === 'answeraudio') return;
        if (payload[key] !== undefined && payload[key] !== null) formData.append(key, payload[key]);
    });
    formData.append('audioOnly', 'true');
    formData.append('answeraudio', payload.answeraudio, payload.answeraudio.name || 'answer.webm');
    try {
        yield INTERVIEW.post('/performCodeEvaluation', formData, {
            headers: { 'content-type': 'multipart/form-data' },
        });
    } catch (error) {
        console.warn('[submitCodingQuestion] recording upload failed; the code is already saved', error);
    }
}

function* submitCodingQuestionRequest(action) {
    const { data, onSuccess, onFailure } = action.payload;
    try {
        // The code goes on its own. Sent together with the recording it was a
        // 4.7 MB request at the median, and a candidate on a slow uplink lost
        // the code with the upload. On its own it is a couple of kilobytes.
        //
        // Each branch keeps the encoding it already used, and only the recording
        // moves out. That is deliberate rather than tidy: multipart turns every
        // value into a string, JSON does not, and codingService compares
        // `input.languageCode === 82` with ===. Posting the multipart case as
        // JSON would start matching that check where it does not match today,
        // and an SQL question would silently switch to a different prompt and a
        // different rating formula. Moving the audio out must not change what a
        // candidate scores.
        const hasRecording = data?.answeraudio instanceof Blob || data?.answeraudio instanceof File;
        // let answerRequest;
        let answerCall;
        if (hasRecording) {
            const formData = new FormData();
            Object.keys(data).forEach((key) => {
                if (key === 'answeraudio') return;
                if (data[key] !== undefined && data[key] !== null) formData.append(key, data[key]);
            });
            // formData.append('answeraudio', data.answeraudio, data.answeraudio.name || 'answer.webm');
            // answerRequest = INTERVIEW.post('/performCodeEvaluation', formData, {
            //     headers: { 'content-type': 'multipart/form-data' },
            // });
            answerCall = startReplayable(() =>
                INTERVIEW.post('/performCodeEvaluation', formData, {
                    headers: { 'content-type': 'multipart/form-data' },
                })
            );
            yield fork(postCodingRecording, data);
        } else {
            // answerRequest = INTERVIEW.post('/performCodeEvaluation', data);
            answerCall = startReplayable(() => INTERVIEW.post('/performCodeEvaluation', data));
        }
        // const res = yield answerRequest;
        const res = yield* submitAnswerWithRetry(answerCall, 'coding answer');
        if (res.status === 200 || res.status === 201) {
            yield put(submitCodingQuestionSuccess());
            if (onSuccess) onSuccess();
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (err) {
        console.error('[submitCodingQuestionRequest] performCodeEvaluation failed:', {
            message: err?.message,
            status: err?.response?.status,
            data: err?.response?.data,
        });
        if (onFailure) onFailure();
        yield put(submitCodingQuestionFailure());
    }
}

function* createCodeSubmissionRequest(action) {
    try {
        const res = yield INTERVIEW.post(
            'codeCompiler/submit?base64_encoded=true&fields=*&wait=true',
            action.payload.data
        );
        if (res.status === 201 || res.status === 200) {
            yield put(createCodeSubmissionSuccess(res.data));
            yield call(action.payload?.callback, res?.data?.result);
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (e) {
        yield put(createCodeSubmissionFailure());
        ToastManager.addToast({
            title: '400 Bad Request',
        });
    }
}

function* lookingForwardRequest(action) {
    try {
        const res = yield INTERVIEW.post('analyze', action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(lookingForwardSuccess());
            yield call(action.payload?.callback, res.data.status);
        } else yield put(lookingForwardFailure());
    } catch (e) {
        yield put(lookingForwardFailure());
    }
}

function* uploadVideoRequest(action) {
    try {
        yield INTERVIEW.post('/uploadVideo', action?.payload);
    } catch (e) {
        console.log(e);
    }
}

function* uploadCefrVideoRequest(action) {
    try {
        yield INTERVIEW.post('/uploadCefrVideo', action?.payload);
    } catch (e) {
        console.log(e);
    }
}

function* fullFaceDetectionRequest(action) {
    const { data, onSuccess, onFailure, doFinally } = action.payload ?? {
        data: null,
        onSuccess: () => {},
        onFailure: () => {},
        doFinally: () => {},
    };
    try {
        const res = yield INTERVIEW.post('detect_faces', data);
        if (res.status === 200) {
            const { hasFullFaceDetected } = res.data ?? {
                hasFullFaceDetected: false,
            };
            yield put(fullFaceDetectionSuccess(hasFullFaceDetected));
            yield call(onSuccess, hasFullFaceDetected);
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (e) {
        yield put(fullFaceDetectionFailure(e));
        yield call(onFailure, e);
    } finally {
        if (doFinally) doFinally();
    }
}

function* postMcqRecording(payload) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
        if (key === 'answeraudio') return;
        if (payload[key] !== undefined && payload[key] !== null) formData.append(key, payload[key]);
    });
    formData.append('audioOnly', 'true');
    formData.append('answeraudio', payload.answeraudio, payload.answeraudio.name || 'answer.webm');
    try {
        yield INTERVIEW.post('submitmcq', formData, {
            headers: { 'content-type': 'multipart/form-data' },
        });
    } catch (error) {
        console.warn('[submitMcq] recording upload failed; the answer is already saved', error);
    }
}

/**
 * The caller may pass onSuccess/onFailure so it can wait for an answer to reach
 * the server before it navigates away. They are how the answer is acknowledged,
 * not part of the answer — and the multipart branches below append every key
 * they find on the payload, so they are removed before anything is put on the
 * wire. A non-object payload is handed back untouched.
 */
const stripAckCallbacks = (payload) => {
    if (!payload || typeof payload !== 'object') return payload;
    // eslint-disable-next-line no-unused-vars
    const { onSuccess, onFailure, ...rest } = payload;
    return rest;
};

export function* submitMcqRequest(action) {
    const { onSuccess, onFailure } = action.payload || {};
    try {
        let res;
        if (action?.payload?.questionType === 'psychometric_assessment') {
            const payload = stripAckCallbacks(action.payload) || {};
            const psychItemType = String(payload.psychometricItemType || '').toLowerCase();
            const isPsychMcqOption =
                psychItemType === 'option' || psychItemType === 'psychometric_mcq' || psychItemType === 'mcq';
            const optionIndex = Number(payload.selectedPsychometricOption ?? payload.selectedOption);
            let candidateAnswer = payload.candidate_answer ?? payload.answer ?? '';
            if (isPsychMcqOption && Number.isInteger(optionIndex) && optionIndex >= 1 && optionIndex <= 26) {
                candidateAnswer = String.fromCharCode(64 + optionIndex);
            }
            const formData = new FormData();
            formData.append('interviewId', payload.interviewId || '');
            formData.append('questionId', payload.questionId || '');
            formData.append('questionType', payload.psychometricItemType || 'option');
            formData.append('question', payload.question ?? '');
            formData.append('role', payload.role || payload.positionTitle || '');
            formData.append('candidate_answer', candidateAnswer);
            formData.append('skill', payload.skill ?? '');
            formData.append('sub_skill', payload.sub_skill || '');
            if (payload.answeraudio instanceof Blob || payload.answeraudio instanceof File) {
                formData.append('answeraudio', payload.answeraudio, payload.answeraudio.name || 'answer.webm');
            }
            res = yield INTERVIEW.post('submitpsychometric', formData, {
                headers: { 'content-type': 'multipart/form-data' },
            });
            // } else if (action.payload?.answeraudio instanceof Blob || action.payload?.answeraudio instanceof File) {
            //     const payload = action.payload;
            //     const formData = new FormData();
            //     Object.keys(payload).forEach((key) => {
            //         if (key === 'answeraudio') return;
            //         if (payload[key] !== undefined && payload[key] !== null) formData.append(key, payload[key]);
            //     });
            //     formData.append('answeraudio', payload.answeraudio, payload.answeraudio.name || 'answer.webm');
            //     res = yield* submitAnswerWithRetry(
            //         () =>
            //             INTERVIEW.post('submitmcq', formData, {
            //                 headers: { 'content-type': 'multipart/form-data' },
            //             }),
            //         'mcq answer'
            //     );
        } else {
            const { answeraudio, ...answerOnly } = stripAckCallbacks(action.payload) || {};
            // const answerRequest = INTERVIEW.post('submitmcq', answerOnly);
            const answerCall = startReplayable(() => INTERVIEW.post('submitmcq', answerOnly));
            if (answeraudio instanceof Blob || answeraudio instanceof File) {
                yield fork(postMcqRecording, { ...answerOnly, answeraudio });
            }
            // res = yield answerRequest;
            res = yield* submitAnswerWithRetry(answerCall, 'mcq answer');
        }
        if (res.status === 200 || res.status === 201) {
            yield put(submitMcqSuccess());
            if (onSuccess) onSuccess();
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        if (onFailure) onFailure();
        yield put(submitMcqFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: GENERAL_ERROR,
            });
    }
}

function* postWrittenRecording(payload) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
        if (key === 'answeraudio') return;
        if (payload[key] !== undefined && payload[key] !== null) formData.append(key, payload[key]);
    });
    formData.append('audioOnly', 'true');
    formData.append('answeraudio', payload.answeraudio, payload.answeraudio.name || 'answer.webm');
    try {
        yield INTERVIEW.post('submitwrittentest', formData, {
            headers: { 'content-type': 'multipart/form-data' },
        });
    } catch (error) {
        console.warn('[submitWrittenTest] recording upload failed; the answer is already saved', error);
    }
}

export function* submitWrittenTestRequest(action) {
    const { onSuccess, onFailure } = action.payload || {};
    try {
        // Same shape as the coding submit: the answer alone first, the recording
        // forked behind it, so a stalled upload cannot take the answer with it.
        // Each branch keeps the encoding it already used — see the note on the
        // coding submit for why the request body is not switched to JSON here.
        //
        // stripAckCallbacks: onSuccess/onFailure arrive on action.payload here and
        // must not reach the wire, so both branches send the stripped copy.
        const strippedPayload = stripAckCallbacks(action.payload);
        const payload = strippedPayload || {};
        const hasRecording = payload?.answeraudio instanceof Blob || payload?.answeraudio instanceof File;
        let answerRequest;
        if (hasRecording) {
            const formData = new FormData();
            Object.keys(payload).forEach((key) => {
                if (key === 'answeraudio') return;
                if (payload[key] !== undefined && payload[key] !== null) formData.append(key, payload[key]);
            });
            // answerRequest = INTERVIEW.post('submitwrittentest', formData, {
            //     headers: { 'content-type': 'multipart/form-data' },
            // });
            answerRequest = startReplayable(() =>
                INTERVIEW.post('submitwrittentest', formData, {
                    headers: { 'content-type': 'multipart/form-data' },
                })
            );
            yield fork(postWrittenRecording, payload);
        } else {
            // strippedPayload, not the `|| {}` copy: the no-recording branch has to
            // put exactly what it puts today on the wire, including nothing at all.
            // answerRequest = INTERVIEW.post('submitwrittentest', strippedPayload);
            answerRequest = startReplayable(() => INTERVIEW.post('submitwrittentest', strippedPayload));
        }
        // const res = yield answerRequest;
        const res = yield* submitAnswerWithRetry(answerRequest, 'written answer');
        if (res.status === 200 || res.status === 201) {
            yield put(submitWrittenTestSuccess());
            if (onSuccess) onSuccess();
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        if (onFailure) onFailure();
        yield put(submitWrittenTestFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: GENERAL_ERROR,
            });
    }
}

export function* submitCefrRequest(action) {
    try {
        let payload = action.payload;
        let config = {};
        // If payload is FormData, set content-type
        if (payload instanceof FormData) {
            config.headers = { 'content-type': 'multipart/form-data' };
        } else if (payload && payload.cefrQuestions) {
            // If payload is object, convert to FormData
            const formData = new FormData();
            formData.append('cefrQuestions', JSON.stringify(payload.cefrQuestions));
            formData.append('interviewId', payload.interviewId);
            payload = formData;
            config.headers = { 'content-type': 'multipart/form-data' };
        }
        const res = yield INTERVIEW.post('submitcefr', payload, config);
        if (res.status === 200 || res.status === 201) {
            yield put(submitCefrSuccess());
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        yield put(submitCefrFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: GENERAL_ERROR,
            });
    }
}

function* compareJDCVRequest(action) {
    let formData = new FormData();
    formData.append('jd', action.payload.jd);
    formData.append('cv', action.payload.cv);
    try {
        const headers = {
            'content-type': 'multipart/form-data',
        };
        if (tenantId && tenantId !== 'undefined' && tenantId !== 'null') {
            headers['x-tenant-id'] = tenantId;
        }
        const res = yield SYSTEM.post(`interviews/match-jd-to-cv`, formData, {
            headers,
        });
        if (res.status === 201 || res.status === 200) {
            yield put(compareJDCVSuccess(res.data));
            // const regex = /(\d+)/;
            const percentage = Number(res.data.matchingPercentage);
            if (percentage <= 0 || !percentage) {
                ToastManager.addToast({
                    title: 'Unable to parse resume, please try again with a different resume.',
                });
            }
        } else {
            ToastManager.addToast({
                title: 'Unable to parse resume, please try again with a different resume.',
            });
        }
    } catch (e) {
        yield put(compareJDCVFailure());
        ToastManager.addToast({
            title: 'Unable to parse resume, please try again with a different resume.',
        });
    }
}

function* getCandidateDetailsUsingResumeRequest(action) {
    let formData = new FormData();
    formData.append('cv', action.payload.resume);
    try {
        const headers = {
            'content-type': 'multipart/form-data',
        };
        const tenantId = localStorage.getItem('tenantId');
        if (tenantId && tenantId !== 'undefined' && tenantId !== 'null') {
            headers['x-tenant-id'] = tenantId;
        }
        const res = yield SYSTEM.post(`interviews/extractDataFromCV`, formData, {
            headers,
        });
        if (res.status === 201 || res.status === 200) {
            yield put(getCandidateDetailsUsingResumeSuccess(res.data));
        }
    } catch (e) {
        yield put(getCandidateDetailsUsingResumeFailure());
    }
}

function* postConsentRequest(action) {
    try {
        const res = yield INTERVIEW.post('consent', {
            consentType: 'interview_consent',
            userType: 'candidate',
            interviewId: action.payload.interviewId,
        });
        if (res.status === 200 || res.status === 201) {
            yield put(postConsentSuccess());
            yield call(action.payload.callback);
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        yield put(postConsentFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: GENERAL_ERROR,
            });
    }
}

function* getConversionReportRequest(action) {
    try {
        const { userId, from, to, signature, recruiterId } = action.payload;
        const url = `incbot/conversion-report/${userId}?from=${from}&to=${to}&signature=${signature}${recruiterId ? `&recruiterId=${recruiterId}` : ''}`;
        const res = yield SYSTEM.get(url);
        if (res.status === 200) {
            yield put(getConversionReportSuccess(res.data.result));
        } else yield put(getConversionReportFailure());
    } catch (e) {
        yield put(getConversionReportFailure());
    }
}

// Saga watcher function to trigger startMeetingRequest on GET_SIGNATURE action
export function* watchUpdateTranscriptAPI() {
    yield takeEvery(UPDATE_TRANSCRIPT, updateTranscriptRequest);
}
export function* watchSubmitTranscribeAPI() {
    yield takeEvery(SUBMIT_TRANSCRIBE, submitTranscribeRequest);
}
export function* watchUpdateConversationQuestionAPI() {
    yield takeEvery(UPDATE_CONVERSATION_QUESTION, UpdateConversationQuestionRequest);
}
export function* watchNotificationErrorAPI() {
    yield takeEvery(NOTIFICATION_ERROR, notificationErrorRequest);
}
export function* watchSwitchTabAPI() {
    yield takeEvery(SWITCH_TAB, switchTabRequest);
}
export function* watchCandidateCallEndAPI() {
    yield takeLeading(CANDIDATE_CALL_END, candidateCallEndRequest);
}
export function* watchContactAdminAPI() {
    yield takeEvery(CONTACT_ADMIN, contactAdminRequest);
}
export function* watchCreateAndUpdateScreenshotsAPI() {
    yield takeEvery(CREATE_AND_UPDATE_SCREENSHOT, createAndUpdateScreenshotsRequest);
}
export function* watchFetchScreenShotImageAPI() {
    yield takeEvery(FETCH_SCREENSHOT_IMAGE, fetchScreenShotImageRequest);
}
export function* watchResendOtpRequestAPI() {
    yield takeEvery(RESEND_OTP_REQUEST, resendOtpRequest);
}
export function* watchCandidateCallStartAPI() {
    yield takeLeading(CANDIDATE_CALL_START, candidateCallStartRequest);
}
export function* watchGetInterviewAPI() {
    yield takeEvery(GET_INTERVIEW, getInterviewRequest);
}
export function* watchGetInterviewTokenAPI() {
    yield takeEvery(GET_INTERVIEW_TOKEN, getInterviewTokenRequest);
}
export function* watchGetQuestionAPI() {
    yield takeEvery(GET_QUESTION, getQuestionRequest);
}
export function* watchPostCandidateAPI() {
    yield takeEvery(POST_CANDIDATE, postCandidateRequest);
}
export function* watchPostInterviewFeedbackAPI() {
    yield takeEvery(POST_INTERVIEW_FEEDBACK, postInterviewFeedbackRequest);
}
export function* watchVerifyCandidateKeyAPI() {
    yield takeEvery(VERIFY_CANDIDATE_KEY, verifyCandidateKeyRequest);
}
export function* watchVerifyRoomAPI() {
    yield takeEvery(VERIFY_ROOM, verifyRoomRequest);
}
export function* watchMovementAPI() {
    yield takeEvery(MOVEMENT, movementRequest);
}
export function* watchInterviewFeedbackStatusAPI() {
    yield takeEvery(INTERVIEW_FEEDBACK_STATUS, interviewFeedbackStatusRequest);
}
export function* watchFullFaceDetectionNewAPI() {
    yield takeEvery(FULL_FACE_DETECTION_NEW, fullFaceDetectionNewRequest);
}
export function* watchGetQuestionNumber() {
    yield takeEvery(GET_QUESTION_NUMBER, getQuestionNumberRequest);
}
export function* watchPostInterviewStatus() {
    yield takeEvery(POST_INTERVIEW_STATUS, postInterviewStatusRequest);
}
export function* watchSubmitCodingQuestion() {
    yield takeEvery(SUBMIT_CODING_QUESTION, submitCodingQuestionRequest);
}
export function* watchCreateCodeSubmissionAPI() {
    yield takeEvery(CREATE_CODE_SUBMISSION, createCodeSubmissionRequest);
}
export function* watchLookingForwardAPI() {
    yield takeEvery(LOOKING_FORWARD, lookingForwardRequest);
}
export function* watchUploadVideoAPI() {
    yield takeEvery(UPLOAD_VIDEO, uploadVideoRequest);
}
export function* watchUploadCefrVideoAPI() {
    yield takeEvery(UPLOAD_CEFR_VIDEO, uploadCefrVideoRequest);
}
export function* watchPracticeQuestionAPI() {
    yield takeEvery(PRACTICE_QUESTION, practiceQuestionRequest);
}
export function* watchPracticeQuestionUpdateAPI() {
    yield takeEvery(PRACTICE_QUESTION_UPDATE, practiceQuestionUpdateRequest);
}
export function* watchFullFaceDetectionAPI() {
    yield takeEvery(FULL_FACE_DETECTION, fullFaceDetectionRequest);
}
export function* watchSubmitMcq() {
    yield takeEvery(SUBMIT_MCQ, submitMcqRequest);
}
export function* watchSubmitWrittenTest() {
    yield takeEvery(SUBMIT_WRITTEN_TEST, submitWrittenTestRequest);
}
export function* watchSubmitCefr() {
    yield takeEvery(SUBMIT_CEFR, submitCefrRequest);
}
export function* watchCompareJDCVAPI() {
    yield takeLeading(COMPARE_JD_CV, compareJDCVRequest);
}
export function* watchGetCandidateDetailsUsingResumeRequestAPI() {
    yield takeEvery(GET_CANDIDATE_DETAILS_USING_RESUME, getCandidateDetailsUsingResumeRequest);
}
export function* watchPostConsentAPI() {
    yield takeLeading(POST_CONSENT, postConsentRequest);
}
export function* watchGetConversionReport() {
    yield takeLeading(GET_CONVERSION_REPORT, getConversionReportRequest);
}
export function* watchLiveKitTokenAPI() {
    yield takeLeading(LIVE_KIT_TOKEN, liveKitTokenRequest);
}
export function* watchGetSessionTokenAPI() {
    yield takeLeading(GET_SESSION_TOKEN, getSessionTokenRequest);
}
export function* watchFaceExpressionAPI() {
    yield takeLeading(FACE_EXPRESSION, faceExpressionRequest);
}
export function* watchSaveVoiceReferenceAPI() {
    yield takeEvery(SAVE_VOICE_REFERENCE, saveVoiceReferenceRequest);
}
export function* watchUploadCandidateVideoAPI() {
    // yield takeLeading(UPLOAD_CANDIDATE_VIDEO, uploadCandidateVideoRequest);
    yield takeEvery(UPLOAD_CANDIDATE_VIDEO, uploadCandidateVideoRequest);
}
export default function* rootSaga() {
    yield all([
        watchSubmitTranscribeAPI(),
        watchSwitchTabAPI(),
        watchCandidateCallEndAPI(),
        watchContactAdminAPI(),
        watchCreateAndUpdateScreenshotsAPI(),
        watchFetchScreenShotImageAPI(),
        watchCandidateCallStartAPI(),
        watchGetInterviewAPI(),
        watchGetInterviewTokenAPI(),
        watchGetQuestionAPI(),
        watchPostCandidateAPI(),
        watchPostInterviewFeedbackAPI(),
        watchVerifyCandidateKeyAPI(),
        watchResendOtpRequestAPI(),
        watchVerifyRoomAPI(),
        watchMovementAPI(),
        watchInterviewFeedbackStatusAPI(),
        watchNotificationErrorAPI(),
        watchFullFaceDetectionNewAPI(),
        watchGetQuestionNumber(),
        watchPostInterviewStatus(),
        watchSubmitCodingQuestion(),
        watchCreateCodeSubmissionAPI(),
        watchLookingForwardAPI(),
        watchUploadVideoAPI(),
        watchPracticeQuestionAPI(),
        watchPracticeQuestionUpdateAPI(),
        watchFullFaceDetectionAPI(),
        watchSubmitMcq(),
        watchSubmitWrittenTest(),
        watchSubmitCefr(),
        watchUpdateConversationQuestionAPI(),
        watchCompareJDCVAPI(),
        watchGetCandidateDetailsUsingResumeRequestAPI(),
        watchPostConsentAPI(),
        watchGetConversionReport(),
        watchLiveKitTokenAPI(),
        watchUpdateTranscriptAPI(),
        watchGetSessionTokenAPI(),
        watchFaceExpressionAPI(),
        watchUploadCandidateVideoAPI(),
        watchUploadCefrVideoAPI(),
        watchSaveVoiceReferenceAPI(),
    ]);
}
